import React, { useState, useEffect } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  Pencil, 
  Trash2, 
  FolderTree,
  Loader2,
  Eye,
  EyeOff,
  X,
  Save,
  Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { unifiedAPI, productTypesAPI } from '../../utils/api';

// ==============================================
// CATEGORY TREE ITEM (Non-recursive flat rendering)
// ==============================================
const CategoryTreeItem = ({ category, level, onEdit, onDelete, onToggleActive, onAddChild, onCopy, isExpanded, onToggleExpand }) => {
  const hasChildren = category.children && category.children.length > 0;
  
  return (
    <div 
      className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-colors group ${
        !category.is_active ? 'bg-ivory-100/50 opacity-60' : 'hover:bg-ivory-50'
      }`}
      style={{ paddingLeft: `${level * 24 + 12}px` }}
    >
      <button
        onClick={() => onToggleExpand(category.id)}
        className={`p-1 rounded hover:bg-ivory-200 ${!hasChildren ? 'invisible' : ''}`}
      >
        {isExpanded ? <ChevronDown className="w-4 h-4 text-ivory-500" /> : <ChevronRight className="w-4 h-4 text-ivory-500" />}
      </button>
      
      <FolderTree className={`w-4 h-4 ${category.is_active ? 'text-gold-500' : 'text-ivory-400'}`} />
      
      <span className={`flex-1 font-body text-sm ${category.is_active ? 'text-ivory-800' : 'text-ivory-500'}`}>
        {category.name}
        {!category.is_active && <span className="ml-2 text-xs bg-ivory-200 text-ivory-500 px-2 py-0.5 rounded">Inactive</span>}
      </span>
      
      <span className="text-xs text-ivory-500 bg-ivory-100 px-2 py-0.5 rounded font-body">
        {category.product_count || 0} products
      </span>
      
      <span className="text-xs text-ivory-400 font-body">L{category.level}</span>
      
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onAddChild(category)} className="p-1.5 rounded hover:bg-ivory-200 text-ivory-500 hover:text-gold-600" title="Add Subcategory">
          <Plus className="w-4 h-4" />
        </button>
        <button onClick={() => onCopy(category)} className="p-1.5 rounded hover:bg-ivory-200 text-ivory-500 hover:text-blue-600" title="Copy to Another Type">
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={() => onToggleActive(category)} className="p-1.5 rounded hover:bg-ivory-200 text-ivory-500 hover:text-gold-600" title={category.is_active ? 'Deactivate' : 'Activate'}>
          {category.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button onClick={() => onEdit(category)} className="p-1.5 rounded hover:bg-ivory-200 text-ivory-500 hover:text-gold-600" title="Edit">
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={() => onDelete(category)} className="p-1.5 rounded hover:bg-red-50 text-ivory-500 hover:text-red-600" title="Delete" disabled={hasChildren}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ==============================================
// CATEGORY MODAL
// ==============================================
const CategoryModal = ({ category, parentCategory, productType, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    is_active: category?.is_active ?? true,
    sort_order: category?.sort_order || 0,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!category?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return alert('Name is required');
    
    setSaving(true);
    try {
      const data = { ...formData, product_type: productType, parent_id: parentCategory?.id || category?.parent_id || null };
      if (isEdit) await unifiedAPI.adminUpdateCategory(category.id, data);
      else await unifiedAPI.adminCreateCategory(data);
      onSave();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        <div className="p-6 border-b border-ivory-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-heading font-semibold text-ivory-800">{isEdit ? 'Edit Category' : 'Add Category'}</h3>
            {parentCategory && <p className="text-sm text-ivory-500 font-body mt-1">Under: <span className="text-gold-600">{parentCategory.name}</span></p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-ivory-100"><X className="w-5 h-5 text-ivory-500" /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Category Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500" required data-testid="category-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ivory-700 mb-1 font-body">Sort Order</label>
            <input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2 rounded-lg border border-ivory-300 bg-ivory-50 text-ivory-800 font-body focus:outline-none focus:ring-2 focus:ring-gold-500" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4 rounded border-ivory-300 text-gold-500" />
            <label htmlFor="is_active" className="text-sm text-ivory-700 font-body">Active (visible on frontend)</label>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-ivory-200">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="btn-luxury-primary" disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Update' : 'Create'}</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==============================================
// COPY CATEGORY MODAL
// ==============================================
const CopyModal = ({ category, currentProductType, productTypes, onCopy, onClose }) => {
  const [targetType, setTargetType] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true); // Default ON per spec
  const [activateCopied, setActivateCopied] = useState(false); // Default OFF per spec
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);
  const hasChildren = category?.children && category.children.length > 0;
  
  const availableTypes = productTypes.filter(t => t.id !== currentProductType);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetType) return alert('Please select a target product type');
    
    setCopying(true);
    setResult(null);
    try {
      const response = await unifiedAPI.adminCopyCategory({
        source_category_id: category.id,
        target_product_type: targetType,
        include_children: includeChildren,
        activate_copied_categories: activateCopied
      });
      setResult(response);
      // Show success notification
      setTimeout(() => {
        onCopy();
      }, 2000);
    } catch (err) {
      alert('Failed to copy: ' + err.message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        <div className="p-6 border-b border-ivory-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-heading font-semibold text-ivory-800">Copy Category</h3>
            <p className="text-sm text-ivory-500 font-body mt-1">Copy &quot;<span className="text-gold-600">{category?.name}</span>&quot; to another product type</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-ivory-100"><X className="w-5 h-5 text-ivory-500" /></button>
        </div>
        
        {result ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Copy className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Copy Complete!</p>
                <p className="text-sm text-green-700">
                  Created: {result.created_count} | Skipped: {result.skipped_count}
                </p>
              </div>
            </div>
            
            {result.skipped && result.skipped.length > 0 && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm font-medium text-yellow-800 mb-2">Skipped Categories:</p>
                <ul className="text-xs text-yellow-700 space-y-1">
                  {result.skipped.map((s, i) => (
                    <li key={i}>• {s.name}: {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="text-sm text-ivory-500 text-center">Closing automatically...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-ivory-700 mb-2 font-body">Target Product Type *</label>
              <div className="grid grid-cols-2 gap-2">
                {availableTypes.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setTargetType(type.id)}
                    className={`px-4 py-3 rounded-lg font-body text-sm font-medium transition-all border ${
                      targetType === type.id 
                        ? 'bg-gold-500 text-white border-gold-500' 
                        : 'bg-ivory-50 text-ivory-700 border-ivory-200 hover:border-gold-300'
                    }`}
                    data-testid={`copy-target-${type.id}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            {hasChildren && (
              <div className="flex items-center gap-3 p-3 bg-ivory-50 rounded-lg">
                <input 
                  type="checkbox" 
                  id="include_children" 
                  checked={includeChildren} 
                  onChange={(e) => setIncludeChildren(e.target.checked)} 
                  className="w-4 h-4 rounded border-ivory-300 text-gold-500" 
                  data-testid="include-children-checkbox"
                />
                <label htmlFor="include_children" className="text-sm text-ivory-700 font-body">
                  Include subcategories ({category?.children?.length || 0} children)
                </label>
              </div>
            )}
            
            <div className="flex items-center gap-3 p-3 bg-ivory-50 rounded-lg">
              <input 
                type="checkbox" 
                id="activate_copied" 
                checked={activateCopied} 
                onChange={(e) => setActivateCopied(e.target.checked)} 
                className="w-4 h-4 rounded border-ivory-300 text-gold-500" 
                data-testid="activate-copied-checkbox"
              />
              <label htmlFor="activate_copied" className="text-sm text-ivory-700 font-body">
                Activate copied categories (default: inactive)
              </label>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
              <p className="font-medium">Note:</p>
              <ul className="text-xs mt-1 space-y-0.5">
                <li>• Only category structure is copied (no products)</li>
                <li>• Duplicate names in target will be skipped</li>
                <li>• New unique slugs will be generated</li>
              </ul>
            </div>
            
            <div className="flex gap-3 justify-end pt-4 border-t border-ivory-200">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="btn-luxury-primary" disabled={copying || !targetType} data-testid="copy-category-btn">
                {copying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Copying...</> : <><Copy className="w-4 h-4 mr-2" />Copy Category</>}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const AdminCategories = () => {
  const [loading, setLoading] = useState(true);
  const [flatCategories, setFlatCategories] = useState([]);
  const [activeProductType, setActiveProductType] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [copyCategory, setCopyCategory] = useState(null);
  const [productTypes, setProductTypes] = useState([]);

  // Load product types from API
  useEffect(() => {
    const loadProductTypes = async () => {
      try {
        const response = await productTypesAPI.getProductTypes({ is_active: true });
        const types = (response.product_types || []).map(pt => ({
          id: pt.slug,
          label: pt.ui?.label_plural || pt.name
        }));
        setProductTypes(types);
        // Set first type as active if not set
        if (types.length > 0 && !activeProductType) {
          setActiveProductType(types[0].id);
        }
      } catch (err) {
        console.error('Failed to load product types:', err);
        // Fallback to hardcoded values
        setProductTypes([
          { id: 'awards', label: 'Awards' },
          { id: 'large_format', label: 'Large Format' },
          { id: 'swag', label: 'Promo Swag' },
          { id: 'small_format', label: 'Small Format' },
        ]);
        setActiveProductType('awards');
      }
    };
    loadProductTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flatten tree for rendering
  const flattenTree = (tree, level = 0) => {
    let result = [];
    for (const cat of tree) {
      result.push({ ...cat, level });
      if (cat.children && cat.children.length > 0 && expandedIds.has(cat.id)) {
        result = result.concat(flattenTree(cat.children, level + 1));
      }
    }
    return result;
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const treeData = await unifiedAPI.adminGetCategoryTree({ product_type: activeProductType });
      const allIds = new Set();
      const collectIds = (cats) => cats.forEach(c => { allIds.add(c.id); if (c.children) collectIds(c.children); });
      collectIds(treeData.tree || []);
      setExpandedIds(allIds);
      setFlatCategories(flattenTree(treeData.tree || []));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (activeProductType) fetchCategories(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProductType]);
  
  useEffect(() => {
    if (!loading && activeProductType) {
      unifiedAPI.adminGetCategoryTree({ product_type: activeProductType }).then(data => {
        setFlatCategories(flattenTree(data.tree || []));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds]);

  const toggleExpand = (id) => setExpandedIds(prev => {
    const newSet = new Set(prev);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    return newSet;
  });

  const handleToggleActive = async (cat) => {
    try {
      await unifiedAPI.adminToggleCategoryActive(cat.id);
      fetchCategories();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const handleDelete = async (cat) => {
    if (cat.children?.length) return alert('Delete children first');
    if (!confirm(`Delete "${cat.name}"?`)) return;
    try {
      await unifiedAPI.adminDeleteCategory(cat.id);
      fetchCategories();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const totalCategories = flatCategories.length;
  const activeCategories = flatCategories.filter(c => c.is_active).length;
  const totalProducts = flatCategories.reduce((s, c) => s + (c.product_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-ivory-800">Categories</h1>
          <p className="text-sm text-ivory-500 font-body mt-1">Manage hierarchical product categories</p>
        </div>
        <Button onClick={() => { setEditingCategory(null); setParentCategory(null); setShowModal(true); }} className="btn-luxury-primary rounded-lg font-body" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />Add Root Category
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-ivory-200 pb-4">
        {productTypes.map(t => (
          <button key={t.id} onClick={() => setActiveProductType(t.id)} className={`px-4 py-2 rounded-lg font-body font-medium transition-all ${activeProductType === t.id ? 'bg-gradient-to-r from-gold-600 to-gold-500 text-white shadow-luxury' : 'bg-ivory-100 text-ivory-600 hover:bg-ivory-200/50 border border-ivory-300/50'}`} data-testid={`category-type-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-ivory-800">{totalCategories}</p>
          <p className="text-sm text-ivory-500 font-body">Total Categories</p>
        </div>
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-green-600">{activeCategories}</p>
          <p className="text-sm text-ivory-500 font-body">Active</p>
        </div>
        <div className="bg-white rounded-xl border border-ivory-200 p-4">
          <p className="text-2xl font-heading font-semibold text-gold-600">{totalProducts}</p>
          <p className="text-sm text-ivory-500 font-body">Products</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-ivory-200 shadow-soft overflow-hidden">
        <div className="p-4 border-b border-ivory-200 bg-ivory-50">
          <h2 className="font-heading font-semibold text-ivory-800 flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-gold-500" />Category Hierarchy
          </h2>
          <p className="text-xs text-ivory-500 font-body mt-1">Click + to add subcategories. Hover to see actions including copy.</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gold-500" /></div>
        ) : flatCategories.length === 0 ? (
          <div className="text-center py-20">
            <FolderTree className="w-12 h-12 text-ivory-300 mx-auto mb-4" />
            <p className="text-ivory-500 font-body">No categories found</p>
          </div>
        ) : (
          <div className="p-4 space-y-1">
            {flatCategories.map(cat => (
              <CategoryTreeItem
                key={cat.id}
                category={cat}
                level={cat.level}
                onEdit={(c) => { setEditingCategory(c); setParentCategory(null); setShowModal(true); }}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
                onAddChild={(c) => { setEditingCategory(null); setParentCategory(c); setShowModal(true); }}
                onCopy={(c) => { setCopyCategory(c); setShowCopyModal(true); }}
                isExpanded={expandedIds.has(cat.id)}
                onToggleExpand={toggleExpand}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CategoryModal
          category={editingCategory}
          parentCategory={parentCategory}
          productType={activeProductType}
          onSave={() => { setShowModal(false); setEditingCategory(null); setParentCategory(null); fetchCategories(); }}
          onClose={() => { setShowModal(false); setEditingCategory(null); setParentCategory(null); }}
        />
      )}

      {showCopyModal && (
        <CopyModal
          category={copyCategory}
          currentProductType={activeProductType}
          productTypes={productTypes}
          onCopy={() => { setShowCopyModal(false); setCopyCategory(null); fetchCategories(); }}
          onClose={() => { setShowCopyModal(false); setCopyCategory(null); }}
        />
      )}
    </div>
  );
};

export default AdminCategories;
